import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import type { BusinessModelCode } from '@iobiz/shared'
import { api } from '../../api/client.js'
import {
  FormulaireBoutique,
  donneesVides,
  slugifier,
  type DonneesBoutique,
} from './FormulaireBoutique.js'

interface Boutique {
  id: string
  slug: string
  name: string
  schemaName: string
  countryCode: string
  countryName: string
  currencyCode: string
  status: string
  businessModel: string
  modules: Record<string, boolean>
  address: string | null
  phone: string | null
  email: string | null
  website: string | null
  legalName: string | null
  taxId: string | null
  logoUrl: string | null
  memberCount: number
  createdAt: string
}

/**
 * Réglages d'une boutique.
 *
 * ⭐ Le formulaire est le MÊME qu'à la création : c'est ce qui garantit qu'on
 * puisse modifier tout ce qu'on a pu saisir. Deux formulaires jumeaux
 * divergent, et l'écart ne se voit qu'au moment où quelqu'un cherche en vain
 * un champ qu'il a rempli la veille.
 */
export function ReglagesBoutiqueDialog({
  storeId,
  onClose,
  onEnregistre,
}: {
  storeId: string | null
  onClose: () => void
  onEnregistre: () => Promise<void>
}): JSX.Element {
  const [boutique, setBoutique] = useState<Boutique | null>(null)
  const [valeurs, setValeurs] = useState<DonneesBoutique>(donneesVides)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoi, setEnvoi] = useState(false)

  useEffect(() => {
    if (!storeId) {
      setBoutique(null)
      return
    }
    setBoutique(null)
    setErreur(null)
    void (async () => {
      try {
        const b = await api.get<Boutique>(`/admin/stores/${storeId}`)
        setBoutique(b)
        setValeurs({
          ...donneesVides(),
          nom: b.name,
          slug: b.slug,
          pays: b.countryCode,
          modele: b.businessModel as BusinessModelCode,
          adresse: b.address ?? '',
          telephone: b.phone ?? '',
          courriel: b.email ?? '',
          siteWeb: b.website ?? '',
          raisonSociale: b.legalName ?? '',
          numeroFiscal: b.taxId ?? '',
          statut: b.status,
          modules: b.modules ?? {},
          logo: b.logoUrl,
        })
      } catch (e) {
        setErreur(e instanceof Error ? e.message : 'Chargement impossible.')
      }
    })()
  }, [storeId])

  return (
    <Dialog open={storeId !== null} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Réglages de la boutique</DialogTitle>
      {/* `dividers` rend le contenu DÉFILANT : sans lui, les derniers blocs
          restent hors de vue sans que rien ne le signale. */}
      <DialogContent dividers sx={{ pt: 3 }}>
        {erreur && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {erreur}
          </Alert>
        )}

        {!boutique ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <FormulaireBoutique
            valeurs={valeurs}
            mode="modification"
            onChange={(maj) => setValeurs((v) => ({ ...v, ...maj }))}
            repere={{
              schemaName: boutique.schemaName,
              devise: boutique.currencyCode,
              membres: boutique.memberCount,
              creeLe: new Date(boutique.createdAt).toLocaleDateString('fr-FR'),
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
        <Button
          variant="contained"
          disabled={!boutique || valeurs.nom.trim().length < 2 || valeurs.slug.length < 2 || envoi}
          onClick={async () => {
            setEnvoi(true)
            setErreur(null)
            try {
              await api.patch(`/admin/stores/${storeId}`, {
                name: valeurs.nom.trim(),
                slug: slugifier(valeurs.slug),
                // ⚠️ Ce champ n'était PAS envoyé : le formulaire le chargeait,
                // l'affichait, le laissait modifier — et l'oubliait au moment
                // d'enregistrer. Le pays revenait donc à sa valeur d'origine à
                // la réouverture, sans le moindre message.
                countryCode: valeurs.pays,
                businessModel: valeurs.modele,
                status: valeurs.statut,
                legalName: valeurs.raisonSociale.trim(),
                taxId: valeurs.numeroFiscal.trim(),
                address: valeurs.adresse.trim(),
                phone: valeurs.telephone.trim(),
                // Chaîne vide = retrait explicite, comme pour le logo : vider
                // le champ doit retirer la mention des documents.
                email: valeurs.courriel.trim(),
                website: valeurs.siteWeb.trim(),
                modules: valeurs.modules,
                // Chaine vide = retrait explicite du logo.
                logoUrl: valeurs.logo ?? '',
              })
              await onEnregistre()
            } catch (e) {
              setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
            } finally {
              setEnvoi(false)
            }
          }}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  )
}
